import {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Autocomplete, CircularProgress, TextField} from '@mui/material';
import {valuePackApi} from 'api/valuePackApi';

const PAGE_SIZE = 20;
const SEARCH_DELAY_MS = 300;
// Load the next page when the list is this close to its bottom (px)
const SCROLL_THRESHOLD = 60;

// Value pack dropdown that loads PAGE_SIZE packs at a time: the next page is
// fetched while the user scrolls the list, and typing searches on the server.
// `value` is the selected pack {id, name} (or null).
// `fetchOptions` swaps the source (e.g. products for product price options):
// ({page, limit, search}) => Promise<{valuePacks: [{id, name}], hasMore}>
const ValuePackPicker = ({
  value,
  onChange,
  onLoaded,
  helperText,
  label,
  fetchOptions,
}) => {
  const noun = (label || 'Value pack').toLowerCase();
  const [options, setOptions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const page = useRef(0);
  // Ignores answers to an older search that arrive after a newer one
  const requestId = useRef(0);

  const loadPage = async (nextPage, term, reset) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(false);
    try {
      const {valuePacks, hasMore: more} = await (
        fetchOptions || valuePackApi.fetchValuePackOptions
      )({
        page: nextPage,
        limit: PAGE_SIZE,
        search: term,
      });
      if (id !== requestId.current) return;
      const list = valuePacks.map(p => ({id: p.id, name: p.name}));
      page.current = nextPage;
      setHasMore(more);
      setOptions(prev => {
        if (reset) return list;
        const seen = new Set(prev.map(p => String(p.id)));
        return [...prev, ...list.filter(p => !seen.has(String(p.id)))];
      });
      if (onLoaded) onLoaded(list);
    } catch {
      if (id === requestId.current) setError(true);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  // First page when the list opens, and again (debounced) on every search
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(
      () => loadPage(1, search, true),
      search ? SEARCH_DELAY_MS : 0,
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search]);

  const handleScroll = event => {
    const list = event.currentTarget;
    const nearBottom =
      list.scrollTop + list.clientHeight >=
      list.scrollHeight - SCROLL_THRESHOLD;
    if (nearBottom && hasMore && !loading) loadPage(page.current + 1, search);
  };

  // The selected pack may not be in the pages loaded so far
  const allOptions =
    value && !options.some(p => String(p.id) === String(value.id))
      ? [value, ...options]
      : options;

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearch('');
      }}
      options={allOptions}
      value={value}
      onChange={(_, selected) => onChange(selected)}
      onInputChange={(_, text, reason) => {
        if (reason === 'input') setSearch(text.trim());
      }}
      // Filtering happens on the server
      filterOptions={x => x}
      getOptionLabel={option => option.name || ''}
      isOptionEqualToValue={(option, selected) =>
        String(option.id) === String(selected.id)
      }
      loading={loading && !options.length}
      loadingText={`Loading ${noun}s...`}
      noOptionsText={
        error ? `Could not load ${noun}s. Try again.` : `No ${noun}s found`
      }
      ListboxProps={{onScroll: handleScroll, style: {maxHeight: 280}}}
      // Always open the list below the field (never flip above it)
      componentsProps={{
        popper: {
          placement: 'bottom-start',
          modifiers: [{name: 'flip', enabled: false}],
        },
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={`Search ${noun}s`}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

ValuePackPicker.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  // Called with each loaded page, so the parent can remember pack names
  onLoaded: PropTypes.func,
  helperText: PropTypes.node,
  label: PropTypes.string,
  fetchOptions: PropTypes.func,
};

ValuePackPicker.defaultProps = {
  value: null,
  onLoaded: undefined,
  helperText: undefined,
  label: 'Value pack',
  fetchOptions: undefined,
};

export default ValuePackPicker;
